import React, { useMemo, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FlowGrid, Button, Row, Separator, Alert, Cell, LabelSpacer, useOverlay, Paragraph } from '@fs/zion-ui'
import ZionForm, { useFieldArray, useWatch, useZionForm, FormTextField, useFormState } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  getDisplayLabelForFieldType,
  getNextUuid,
} from '@fs/zion-sls'
import { ContentAdd } from '@fs/zion-icon'
import { yupResolver } from '@hookform/resolvers/yup'
import getAdditionalFactDispatches from './utils/saveAdditionalFacts'
import { FullTextApp, headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import HighlightButton from '../panels/shared/HighlightButton'
import { prepareFieldForHighlights } from '../shared/utils/highlightUtils'
import { getAdditionalFactOptions, prepareAdditionalFactDefaultValues } from './utils/additionalFacts.utils'
import FilterFactOverlay from './components/FilterFactOptionsOverlay'
import CardFooter from '../shared/components/CardFooter'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useDisplayHighlights } from '../../../hooks/useDisplayHighlights'
import LinkNamesButton from '../panels/shared/LinkNamesButton'
import { useLinkNamesSave } from '../panels/linkNamesPanel/hooks/useLinkNamesSave'
import FormStandardizedSexField from '../../standardizedSexField/StandardizedSexField'
import { validationSchema } from './utils/validationSchema'

const SEX_CODE = 'SEX_CODE'
const AGE = 'AGE'
const OCCUPATION = 'OCCUPATION'
const OTHER_FACTS_KEY = 'OTHER_FACTS'

/**
 * AdditionalFactsCardEditMode component provides an editable interface for managing
 * additional facts associated with a person record. It includes form controls, validation,
 * highlighting capabilities, and support for linked names across multiple records.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.person - Person object containing record data
 * @param {string} props.person.id - Unique identifier for the person
 * @param {string} props.person.recordId - Record identifier for the person
 * @param {boolean} props.reviewed - Whether the card has been reviewed
 * @param {Function} props.setReviewed - Callback to update the reviewed state
 * @param {Function} props.onSave - Callback function invoked when saving changes with formValues, dispatches, and sls
 * @param {Function} props.setEditMode - Callback to update the edit mode state
 * @param {Array} props.additionalFacts - Array of additional fact objects to display and edit
 * @param {Function} [props.onDirtyChange] - Callback function invoked when form dirty state changes
 * @param {boolean} [props.saveCardDataToSls=false] - Whether to save card data to SLS backend
 * @param {string}   [props.splitScreenPartitionKey]        Split screen partition key if using split screen.
 * @returns {React.ReactElement} The rendered edit mode form component
 */
const AdditionalFactsCardEditMode = ({
  person,
  reviewed,
  setReviewed,
  onSave,
  setEditMode,
  additionalFacts,
  onDirtyChange,
  saveCardDataToSls = false,
  splitScreenPartitionKey,
  genderTranslations,
}) => {
  const [t] = useTranslation()
  const slsState = useSlsStateContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])
  const { getHighlights, getDispatches, commitEdits, clearEdits } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  const factOptions = useMemo(() => getAdditionalFactOptions(slsState.types), [slsState.types])

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues: prepareAdditionalFactDefaultValues(additionalFacts, person, genderTranslations, slsState.types),
    resolver: yupResolver(validationSchema),
  })

  const fieldListOverlay = useOverlay()
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState(null)
  const slsDispatch = useSlsDispatchContext()

  const fieldArrayMethods = useFieldArray({
    name: 'additionalFacts',
    control: formMethods.control,
    keyName: 'formId', // to avoid conflict with 'id' from our data
  })
  const { fields } = fieldArrayMethods

  const watchedAdditionalFacts = useWatch({
    control: formMethods.control,
    name: 'additionalFacts',
  })

  const normalizedAdditionalFacts = useMemo(() => watchedAdditionalFacts || [], [watchedAdditionalFacts])

  const factTypeMap = useMemo(() => {
    return (slsState?.types?.fields || []).reduce((acc, type) => {
      acc[type.fieldType] = type
      return acc
    }, {})
  }, [slsState?.types?.fields])

  const getFieldLabel = useCallback(
    (fieldType, fallbackLabel) => {
      return (
        getDisplayLabelForFieldType(fieldType, slsState.types) ||
        factTypeMap[fieldType]?.displayLabel ||
        fallbackLabel ||
        fieldType
      )
    },
    [factTypeMap, slsState.types]
  )

  const appendFieldByType = useCallback(
    (fieldType, fallbackLabel) => {
      const existingIndex = normalizedAdditionalFacts.findIndex((fact) => fact.key === fieldType)
      if (existingIndex !== -1) return

      fieldArrayMethods.append({
        label: getFieldLabel(fieldType, fallbackLabel),
        value: '',
        key: fieldType,
        id: getNextUuid(),
        editable: factTypeMap[fieldType]?.editable ?? true,
        locked: false,
        added: true,
        dispatches: [],
      })
    },
    [factTypeMap, fieldArrayMethods, getFieldLabel, normalizedAdditionalFacts]
  )

  const suggestedFacts = useMemo(
    () => [
      { key: AGE, label: getFieldLabel(AGE, t('additional-facts-card.suggested.age', 'Age')) },
      {
        key: OCCUPATION,
        label: getFieldLabel(OCCUPATION, t('additional-facts-card.suggested.occupation', 'Occupation')),
      },
      { key: OTHER_FACTS_KEY, label: t('additional-facts-card.other-facts', 'Other Facts') },
    ],
    [getFieldLabel, t]
  )

  const sexFieldIndex = useMemo(
    () => normalizedAdditionalFacts.findIndex((fact) => fact.key === SEX_CODE),
    [normalizedAdditionalFacts]
  )

  const defaultSexValue = useMemo(
    () => formMethods.formState.defaultValues?.additionalFacts?.[sexFieldIndex]?.value,
    [formMethods.formState.defaultValues, sexFieldIndex]
  )

  const visibleSuggestedFacts = useMemo(() => {
    const factsInForm = watchedAdditionalFacts?.map((fact) => fact.key) || []

    return suggestedFacts.filter((fact) => !factsInForm.includes(fact.key))
  }, [suggestedFacts, watchedAdditionalFacts])

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Display highlights for all fields in edit mode
  const fieldIds = useMemo(() => fields.map((f) => f.id), [fields])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: !['editSharedHighlightPage1', 'editSharedHighlightPage2', 'highlightsPanel'].includes(topMostPanelId),
  })

  const onSubmit = async (formValues) => {
    try {
      // Generate dispatches - this will throw if validation fails
      const factsDispatches = getAdditionalFactDispatches({
        additionalFacts: formValues.additionalFacts,
        defaultValues: formMethods.formState.defaultValues,
        person,
        images: slsState.images,
        partitionKey: slsState.currentImageArk,
        highlights: getHighlights().mainEditHighlights,
      })

      // Get highlight dispatches from the highlights context
      const highlightDispatches = getDispatches({
        mainImageArk: slsState.currentImageArk,
        splitScreenImageArk: splitScreenPartitionKey,
      })

      // Combine all dispatches
      const dispatches = [...factsDispatches, ...highlightDispatches]
      const sls = generateSls(dispatches)

      setSaving(true)

      if (saveCardDataToSls) {
        await saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          slsActionsSplunkDebugger,
          dispatch: slsDispatch,
          appName: FullTextApp,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            // Processing SLS dispatches to update local state even if the saveChangesFlag is off
            if (!saveChangesFlag.isOn) {
              slsDispatch({
                type: 'BATCH_DISPATCHES',
                value: dispatches,
              })
            }

            setSaving(false)
            setReviewed?.(true)
            onSave(formValues, dispatches, sls)
            commitEdits()
          },
          errorHandler: (err) => {
            setError(err)
            errorCollector(err, null, {
              codeId: 92,
              sls,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
            setSaving(false)
          },
          saveInitiatorFilename: 'AdditionalFactsCard.js',
        })
      } else {
        // Processing SLS dispatches to update local state even if the saveChangesFlag is off
        if (!saveChangesFlag.isOn) {
          slsDispatch({
            type: 'BATCH_DISPATCHES',
            value: dispatches,
          })
        }

        setSaving(false)
        setReviewed?.(true)
        onSave(formValues, dispatches, sls)
        commitEdits()
      }
    } catch (err) {
      // Validation errors (duplicate relationships, missing data, etc.)
      setError(err)
      errorCollector(err, null, {
        codeId: 93,
        message: 'Additional Facts validation error',
      })
    }
  }

  const handleHighlightSave = useCallback(
    (fieldName, index) => (data) => {
      if (data?.newFieldId && data?.newFieldValue) {
        const currentFieldData = formMethods.getValues(fieldName)
        const superElements = data.selectedPersonIds?.map((id) => ({ partitionKey: slsState.currentImageArk, id }))

        fieldArrayMethods.update(index, {
          ...currentFieldData,
          id: data.newFieldId,
          value: data.newFieldValue,
          dispatches: data?.dispatches || [],
          superElements,
        })
      }
    },
    [formMethods, fieldArrayMethods, slsState.currentImageArk]
  )

  // Use the hook for handling link names save with field array update strategy
  const { handleLinkNamesSave } = useLinkNamesSave({
    getValues: formMethods.getValues,
    updateField: fieldArrayMethods.update,
    images: slsState.images,
    currentImageArk: slsState.currentImageArk,
  })

  const handleSuggestedFactClick = useCallback(
    (factKey, fallbackLabel) => {
      if (factKey === OTHER_FACTS_KEY) {
        fieldListOverlay.open()
        return
      }

      appendFieldByType(factKey, fallbackLabel)
    },
    [appendFieldByType, fieldListOverlay]
  )

  return (
    <ZionForm methods={formMethods} onSubmit={onSubmit}>
      <Separator size="xs" />
      <FlowGrid orientation="vertical" columnCount={1} gutters="pico">
        <Paragraph size="sm">
          {t('additional-facts-card.description', 'What details about this person are shown on the document?')}
        </Paragraph>
        {fields.map((field, index) => (
          <div key={field.formId}>
            <Row gutters="xxs">
              {field?.key === 'SEX_CODE' ? (
                <FormStandardizedSexField
                  fieldName={`additionalFacts[${index}].value`}
                  addedSexCode={!defaultSexValue ? { id: SEX_CODE } : null}
                  person={person}
                  autosuggestProps={{
                    label: normalizedAdditionalFacts[sexFieldIndex]?.label || t('edit-essential-info.sex-code', 'Sex'),
                    'data-testid': 'additional-facts-sex-field',
                    ignoreTouchedForWarnings: true,
                  }}
                  optionKeyName="id"
                  growX
                  useDropdown
                />
              ) : (
                <FormTextField
                  name={`additionalFacts[${index}].value`}
                  label={field.label}
                  value={field.value}
                  warningValidate={(val) =>
                    field.superElements?.length > 1 &&
                    val !== formMethods.formState.defaultValues?.additionalFacts?.[index]?.value
                      ? t(
                          'additional-facts-card.linked-field-change-warning',
                          'Changes will be made on all who are linked.'
                        )
                      : null
                  }
                  onChange={(ev) => {
                    return formMethods.setValue(`additionalFacts[${index}]`, {
                      ...normalizedAdditionalFacts[index],
                      value: ev.target.value,
                    })
                  }}
                  disabled={!field.editable}
                  growX
                  dense={field?.linkedPersonIds?.length > 1}
                />
              )}
              <Cell>
                <LabelSpacer />
                <HighlightButton
                  field={prepareFieldForHighlights(
                    formMethods.getValues,
                    field.key,
                    `additionalFacts[${index}]`,
                    slsState.types,
                    slsState.images
                  )}
                  splitScreenImageArk={splitScreenPartitionKey}
                  buttonProps={{ 'data-testid': `${field.key}-highlight-button` }}
                  linkName={`Additional Facts ${field.key} Highlight`}
                  disabled={!field.editable}
                  onSave={handleHighlightSave(`additionalFacts.${index}`, index)}
                  saveToSls={!field.added}
                  growX
                  iconOnly
                />
              </Cell>
            </Row>
            {field.shared && (
              <LinkNamesButton
                field={field}
                person={person}
                onSave={handleLinkNamesSave(index)}
                linkedPersonIds={field.linkedPersonIds || []}
              />
            )}
          </div>
        ))}
        <Row wrap guttersX="xs" guttersY="xxs">
          {visibleSuggestedFacts.map((fact) => (
            <Button
              key={fact.key}
              Icon={ContentAdd}
              emphasis="low"
              data-testid={`add-suggested-fact-${fact.key.toLowerCase()}`}
              linkName={`Additional Facts Suggestion ${fact.key}`}
              onClick={() => handleSuggestedFactClick(fact.key, fact.label)}
            >
              {fact.label}
            </Button>
          ))}
        </Row>
      </FlowGrid>
      <FilterFactOverlay
        fieldListOverlay={fieldListOverlay}
        factOptions={factOptions}
        fieldArrayMethods={fieldArrayMethods}
      />
      {error && (
        <Alert type="error">
          {t('additional-facts-card.save-error-msg', 'Error saving data. Please try again later.')}
        </Alert>
      )}
      <Separator size="sm" />
      <CardFooter
        reviewed={reviewed}
        saving={saving}
        onCancel={() => {
          onDirtyChange?.(false)
          setEditMode(false)
          clearEdits()
        }}
        label="additional-facts"
        customLinkNamePrefix="Additional Facts Footer"
      />
    </ZionForm>
  )
}

AdditionalFactsCardEditMode.propTypes = {
  person: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recordId: PropTypes.string.isRequired,
  }).isRequired,
  reviewed: PropTypes.bool,
  setReviewed: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  setEditMode: PropTypes.func.isRequired,
  additionalFacts: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      value:
        PropTypes.string ||
        PropTypes.shape({
          primaryText: PropTypes.string,
          key: PropTypes.string,
        }),
      superElements: PropTypes.arrayOf(PropTypes.shape({})),
    })
  ).isRequired,
  onDirtyChange: PropTypes.func,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  genderTranslations: PropTypes.object,
}

export default AdditionalFactsCardEditMode
